// BalanceExportPDF.jsx
import React from 'react'
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer'

// ===== Helpers
const toTons = (kg = 0) => Number(kg || 0) / 1000
const fmt = (n = 0) => Number(n || 0).toFixed(2).replace('.', ',') // estilo ES "24,84"
const tmBruto = (n = 0) => `${fmt(n)} TM/Bruto`

// ===== Estilos
const styles = StyleSheet.create({
  page: {
    paddingTop: 36,
    paddingBottom: 40,
    paddingHorizontal: 40,
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: '#111',
  },
  title: {
    fontSize: 24,
    textAlign: 'center',
    fontWeight: 900,
    textTransform: 'uppercase',
    marginBottom: 7,
  },
  prevBalanceRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 7,
  },
  prevBalanceText: { fontSize: 12 },

  monthHeader: {
    fontSize: 12,
    fontWeight: 700,
    paddingRight: 14,
    textTransform: 'uppercase',
  },

  summary: {
    backgroundColor: '#efefef',
    paddingVertical: 6,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  summaryItemBold: { fontWeight: 700 },
  pipe: { marginHorizontal: 8 },

  sectionLabel: {
    fontWeight: 700,
    marginTop: 4,
    marginBottom: 6,
    textAlign: 'center',
    textTransform: 'uppercase',
  },

  table: {
    borderWidth: 0.6,
    borderColor: '#ddd',
    borderRadius: 2,
  },
  thead: {
    backgroundColor: '#f5f5f5',
    flexDirection: 'row',
    borderBottomWidth: 0.6,
    borderBottomColor: '#ddd',
  },
  th: {
    fontSize: 8,
    paddingVertical: 5,
    paddingHorizontal: 5,
    fontWeight: 700,
  },
  tbody: { },
  tr: {
    flexDirection: 'row',
    borderBottomWidth: 0.6,
    borderBottomColor: '#eee',
  },
  td: {
    fontSize: 8,
    paddingVertical: 5,
    paddingHorizontal: 5,
  },

  // anchos de columnas (aprox. como en la imagen)
  colDate:   { width: '18%' },
  colNature: { width: '27%' },
  colBiz:    { width: '40%' },
  colQty:    { width: '15%', textAlign: 'right' },

  hrStrong: {
    height: 1.2,
    backgroundColor: '#111',
    marginTop: 6,
    marginBottom: 6,
  },

  monthFooter: {
    backgroundColor: '#000',
    marginTop: 0,
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  monthFooterText: {
    color: '#fff',
    fontWeight: 700,
    paddingHorizontal: 12,
    paddingVertical: 4
  },

  // util
  mb4: { marginBottom: 4 },
  mb8: { marginBottom: 6 },
})

export default function BalanceExportPDF({ balance = [], initialBalance = 0, t }) {
  // acumulado que se va actualizando por mes
  let accumulated = toTons(initialBalance)

  const tt = (k, fallback) => (t ? t(k) : fallback)

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        {/* Título */}
        <Text style={styles.title}>{tt('admin.movements.balance', 'Balance')}</Text>

        {/* Saldo anterior */}
        <View style={styles.prevBalanceRow}>
          <Text style={styles.prevBalanceText}>
            {tt('admin.movements.initialBalance', 'Saldo anterior')}: {tmBruto(accumulated)}
          </Text>
        </View>

        {/* Secciones por mes */}
        {balance?.map((m, idx) => {
          const monthBalance = toTons(m.totalEntries) - toTons(m.totalOutputs)
          accumulated = accumulated + monthBalance

          return (
            <View key={`${m.year}-${m.month}-${idx}`} wrap={false}>
              {/* Línea de totales del mes */}
              <View style={styles.summary}>
                <Text style={styles.monthHeader}>
                  {tt('months.' + m.month, m.month)} {m.year}
                </Text>
                <Text style={styles.summaryItemBold}>
                  {tt('admin.movements.entries', 'Entradas')}:
                </Text>
                <Text>{tmBruto(toTons(m.totalEntries))}</Text>

                <Text style={styles.pipe}>|</Text>

                <Text style={styles.summaryItemBold}>
                  {tt('admin.movements.outputs', 'Salidas')}:
                </Text>
                <Text>{tmBruto(toTons(m.totalOutputs))}</Text>

                {/* <Text style={styles.summaryItemBold}>
                  {tt('admin.movements.wastes', 'Mermas')}:
                </Text>
                <Text>{tmBruto(toTons(m.totalWastes))}</Text> */}
              </View>

              {/* Salidas */}
              <Text style={styles.sectionLabel}>
                {tt('admin.movements.outputs', 'Salidas')}
              </Text>

              <View style={[styles.table]}>
                {/* THead */}
                <View style={styles.thead}>
                  <Text style={[styles.th, styles.colDate]}>{tt('admin.movements.date', 'Fecha')}</Text>
                  <Text style={[styles.th, styles.colNature]}>{tt('admin.movements.nature', 'Naturaleza')}</Text>
                  <Text style={[styles.th, styles.colBiz]}>{tt('admin.movements.business', 'Proveedor')}</Text>
                  <Text style={[styles.th, styles.colQty]}>{tt('global.toneladasbruto', 'TM/Bruto')}</Text>
                </View>

                {/* TBody */}
                <View style={styles.tbody}>
                  {(m.outputs && m.outputs.length > 0) ? m.outputs.sort((a, b) => new Date(a.date) - new Date(b.date)).map((o) => (
                    <View key={o._id} style={styles.tr}>
                      <Text style={[styles.td, styles.colDate]}>{`${new Date(o.date).getDate().toString().padStart(2, '0')}/${(new Date(o.date).getMonth() + 1).toString().padStart(2, '0')}/${new Date(o.date).getFullYear()}`}</Text>
                      <Text style={[styles.td, styles.colNature]}>{o.nature}</Text>
                      <Text style={[styles.td, styles.colBiz]}>{o.business}</Text>
                      <Text style={[styles.td, styles.colQty]}>{fmt(toTons(o.quantity))}</Text>
                    </View>
                  )) : (
                    <View style={styles.tr}>
                      <Text style={[styles.td, { width: '100%' }]}>
                        {tt('admin.movements.thereisnotmovements', 'No hay movimientos.')}
                      </Text>
                    </View>
                  )}
                </View>
              </View>

              {/* Si hay mermas, mostramos una tabla similar */}
              {m.wastes && m.wastes.length > 0 && (
                <>
                  <Text style={styles.sectionLabel}>
                    {tt('admin.movements.wastes', 'Mermas')}:
                  </Text>

                  <View style={[styles.table, styles.mb8]}>
                    <View style={styles.thead}>
                      <Text style={[styles.th, styles.colDate]}>{tt('admin.movements.date', 'Fecha')}</Text>
                      <Text style={[styles.th, styles.colNature]}>{tt('admin.movements.nature', 'Naturaleza')}</Text>
                      <Text style={[styles.th, styles.colBiz]}>{tt('admin.movements.business', 'Proveedor')}</Text>
                      <Text style={[styles.th, styles.colQty]}>{tt('global.toneladasbruto', 'TM/Bruto')}</Text>
                    </View>
                    <View style={styles.tbody}>
                      {m.wastes.map((w) => (
                        <View key={w._id} style={styles.tr}>
                          <Text style={[styles.td, styles.colDate]}>{new Date(w.date).toLocaleDateString('es-ES')}</Text>
                          <Text style={[styles.td, styles.colNature]}>{w.nature}</Text>
                          <Text style={[styles.td, styles.colBiz]}>{w.business}</Text>
                          <Text style={[styles.td, styles.colQty]}>{fmt(toTons(w.quantity))}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                </>
              )}

              {/* línea fuerte + pie de mes centrado */}
              <View style={styles.monthFooter}>
                <Text style={styles.monthFooterText}>
                  {tt('admin.movements.balanceMonth', 'Saldo del mes')}: {tmBruto(monthBalance)}   |   {tt('admin.movements.accumulated', 'Saldo')}: {tmBruto(accumulated)}
                </Text>
              </View>
            </View>
          )
        })}

      </Page>
    </Document>
  )
}
